<template>
  <lp-base-select
    :disabled="isDisabled"
    search
    :label-top="label"
    :placeholder="placeholder"
    :message="message"
    :required="required"
    :clearable="clearable"
    :is-options-group="isOptionsGroup"
    v-bind="$attrs"
    @search-emit="onSearchEmit"
  >
    <template
      v-if="selectionSize || (isOptionsGroup && displayValue)"
      #placeholder
    >
      {{ displayValue }}
    </template>
    <slot />
  </lp-base-select>
</template>

<script setup lang="ts">
import { computed, toRef, provide, ref, reactive } from 'vue'
import { TOption } from '@/components/select/types'
import { AllowedTypes } from '@/components/alert/constants'
import { Colors } from '@/models/enums'
import { t } from '@/plugins/i18n'

defineOptions({
  name: 'lp-multiselect',
  inheritAttrs: false,
})

interface Props {
  type?: AllowedTypes
  placeholder?: string
  label?: string
  trackBy?: string
  labelBy?: string
  message?: string
  groupValue?: string | null
  modelValue: string | number | string[] | object[] | null
  disabled?: boolean
  required?: boolean
  clearable?: boolean
  searchFilter?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  type: 'primary',
  placeholder: t('Shared.SelectOption'),
  label: t('Shared.All'),
  trackBy: 'id',
  labelBy: 'label',
  message: '',
  groupValue: null,
  modelValue: null,
  disabled: false,
  required: false,
  clearable: true,
  searchFilter: true,
})
const emit = defineEmits([
  'update:model-value',
  'update:group-value',
  'update:model-label',
  'update:searchValue',
])

const allOptionsMap = new Map()
const modelValue = toRef(props, 'modelValue')
const selectionMap = computed(() => new Set<Object>(modelValue.value))
const searchValue = reactive({
  value: '',
})
const isOptionsGroup = computed(() => {
  return Boolean(props.groupValue)
})
const modelValueLabel = ref('')
const handleModelChange = (
  value: unknown,
  model: unknown,
  label: string,
  group: string,
) => {
  modelValueLabel.value = label
  emit('update:model-value', value, model, label, group)
}
const handleGroupChange = (group: string, value: unknown, option: TOption) => {
  emit('update:group-value', group, value, option)
}
const handleLabelChange = (e: string) => {
  modelValueLabel.value = e
}
provide('wrapperValue', toRef(props, 'modelValue'))
provide('modelGroupValue', toRef(props, 'groupValue'))
provide('emitHandlers', {
  handleGroupChange,
  handleModelChange,
  handleLabelChange,
})
provide('addOptions', addOptions)
provide('onToggle', onToggle)
provide('onGroupToggle', onGroupToggle)
provide('isSelected', isSelected)
provide('getLabel', getLabel)
provide('isGroupSelected', isGroupSelected)
provide(
  'searchEmitValue',
  computed(() => searchValue.value?.toLowerCase()),
)
provide('searchFilter', toRef(props, 'searchFilter'))
provide('getOptionType', getOptionType)

function getOptionType(option: object) {
  return option.type || Colors.PRIMARY
}

// Keep track of all que options available from every child component. This method should
// only be invoked once per child component
function addOptions(options: object[]) {
  options.forEach((option) => {
    allOptionsMap.set(getValue(option), option)
  })
}

function onToggle(option: object) {
  if (isSelected(option)) {
    removeOption(option)
  } else {
    addOption(option)
  }
  triggerOnChange()
}

function isSelected(option: object) {
  return selectionMap.value.has(getValue(option))
}

function isGroupSelected(options: object[], groupValue?: object) {
  if (!options.length) {
    // IMPORTANT: We're assuming that groupValue has same trackBy ID than the selected options
    return groupValue ? isSelected(groupValue) : false
  }

  return options.every(isSelected)
}

function getLabel(option: object) {
  return option[props.labelBy as keyof object]
}

function getValue(option: object) {
  return option[props.trackBy as keyof object]
}

function triggerOnChange() {
  const ids = Array.from(selectionMap.value)
  const models = ids.map((id) => allOptionsMap.get(id))
  emit('update:model-value', ids, models)
}

function removeOption(option: object) {
  selectionMap.value.delete(getValue(option))
}

function addOption(option: object) {
  selectionMap.value.add(getValue(option))
}

function onGroupToggle(toggle: boolean, options: object[], parentId: number) {
  options
    .filter((option) => !option.disabled)
    .forEach(toggle ? removeOption : addOption)
  if (parentId) {
    if (toggle) {
      removeOption({ [props.trackBy]: parentId })
    } else {
      addOption({ [props.trackBy]: parentId })
    }
  }
  triggerOnChange()
}

const onSearchEmit = (value: string) => {
  searchValue.value = value
  emit('update:searchValue', searchValue.value)
}

const isDisabled = computed(() => {
  return props.disabled
})

const selectionSize = computed(() => modelValue.value?.length)

const displayValue = computed(() => {
  if (isOptionsGroup.value) {
    if (modelValueLabel.value) {
      return modelValueLabel.value
    }
    return modelValue.value && typeof modelValue.value === 'object'
      ? modelValue.value[props.labelBy]
        ? modelValue.value[props.labelBy]
        : modelValue.value.name
      : modelValue.value
  }

  return `(${selectionSize.value}) Selected`
})
</script>

<style>
.lp-select-dropdown .lp-checkbox-wrapper {
  margin-bottom: 0.75rem;
}

.lp-select-dropdown
  ul
  .lp-checkbox-group-wrapper
  > .lp-checkbox-wrapper
  .lp-checkbox-label {
  text-transform: uppercase;
  font-weight: 500;
}
</style>
